/* eslint-disable */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Play, Send, AlertTriangle, CheckCircle2, Lock, FileText, Eye, Loader2, Shield, ShieldCheck, Download, Paperclip, MessageSquare, X, Trash2, FileSpreadsheet, FileSignature, FileType2, Image as ImageIcon, FileImage, Upload, Sparkles, Grid3X3, Layers, ChevronDown, ChevronUp } from "lucide-react";
import api, { formatErr } from "../lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import MicButton from "./MicButton";
import SimulationViewer from "./SimulationViewer";
import DesignBasisWizard from "./DesignBasisWizard";
import ErrorBoundary from "./ErrorBoundary";
import StageRelationshipsPanel from "./StageRelationshipsPanel";
import DeliverablesPicker from "./DeliverablesPicker";
import { HazopWorksheet, SilLopaWorksheet, TbeWorksheet, LoopDiagramsPanel, FireWaterPanel } from "./WorksheetPanels";
import DisciplineCanvasEditor from "./DisciplineCanvasEditor";
import SldDocumentRegister from "./diagram/SldDocumentRegister";
import ApsDrawingViewer from "./diagram/ApsDrawingViewer";
import PlotPlanEditor from "./PlotPlanEditor";
import CEMatrixEditor from "./CEMatrixEditor";
import HookUpPanel from "./HookUpPanel";
import Model3DEditor from "./Model3DEditor";
import EquipmentLayoutEditor from "./EquipmentLayoutEditor";
import ProcessPlantA6View from "./process_plant/ProcessPlantA6View";
import AIAssessmentCard from "./AIAssessmentCard";
import AsyncDocxButton from "./AsyncDocxButton";
import AiGenerateButton from "./AiGenerateButton";
// Wave CC — PFD / P&ID editor wiring (user issues #2 + #3)
import PfdDocumentFirstPanel from "./diagram/PfdDocumentFirstPanel";
import PidDocumentRegister from "./diagram/PidDocumentRegister";
// Stage B Final Pass — External sim import (B.1) + Drawing template upload (B.2)
import SimulationImportPanel from "./SimulationImportPanel";
import DrawingTemplatePanel from "./DrawingTemplatePanel";
import StageCPanel from "./StageCPanel";
import StageDPanel from "./StageDPanel";
import StageEPanel from "./StageEPanel";
import StageFPanel from "./StageFPanel";
import StageGPanel from "./StageGPanel";
import StageHPanel from "./StageHPanel";
import StageIPanel from "./StageIPanel";
import StageJPanel from "./StageJPanel";
// Wave CO — Universal Drawings Register (mirrors B.2 / B.3 UX for
// stages C, D, E, F, G, H, I, J — one tab listing every CAD-style
// SVG produced by the stage with View + Edit jumps).
import StageDrawingsRegister from "./diagram/StageDrawingsRegister";

// Maps each stage code that exposes a Drawings tab to the existing
// editor sub-tab key used for the "Edit" jump in the register. Codes
// without an editor map to null (the Edit button is hidden).
const DRAWINGS_TAB_REGEX = /^([CDEFGHIJ])(\.\d+)?$/;
const DRAWINGS_EDITOR_JUMPS = {
  "D.1": "plot-plan", "D.2": "d2-view", "D.3": "piping-3d",
  "E.2": "hazarea-editor", "E.3": "sld-editor",
  "E.4": "earthing-editor", "E.5": "cable-tray-editor",
  "F.1": "control-arch-editor", "F.2": "instrument-editor",
  "F.3": "ce-matrix", "F.4": "loop-editor", "F.5": "hook-up",
  "G.2": "foundation-editor", "G.3": "steel-editor",
  "G.4": "rc-editor",
  "H.1": "fire-water-layout", "H.2": "detection-editor",
  "H.3": "suppression-editor",
  "J.1": "j1-master-3d",
};
// Wave CD — D.2 mature plot plan view (3D + Cesium reused from A.6)
import ProcessPlantD2View from "./process_plant/ProcessPlantD2View";
import J1FederatedModelView from "./process_plant/J1FederatedModelView";
import { useT } from "../lib/i18n";

const STATUS_COLOR = {
  not_started: "var(--pf-text-dim)",
  in_progress: "var(--pf-spark)",
  complete: "var(--pf-ok)",
  approved: "var(--pf-accent)",
  blocked: "var(--pf-err)",
};

export default function StagePanel(props) {
  // Wrap the real panel in an ErrorBoundary so a single mis-typed prop or
  // a bad API payload on one stage code can't take out the whole workspace.
  return (
    <ErrorBoundary>
      <StagePanelInner {...props} />
    </ErrorBoundary>
  );
}

function StagePanelInner({ projectId, stage, onChange, approvalGatesEnabled, onOpenTab, onSendHandover, extraTabs, domain }) {
  const t = useT();
  const [deps, setDeps] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [running, setRunning] = useState(false);
  // Wave BI — granular stage progress (current_step/total_steps/percent/message)
  const [progress, setProgress] = useState(null);
  const [busyApprove, setBusyApprove] = useState(false);
  const [openDeliv, setOpenDeliv] = useState(null);
  const [err, setErr] = useState("");
  const [pollTick, setPollTick] = useState(0);
  const [attachments, setAttachments] = useState([]);
  const [comments, setComments] = useState([]);
  const [validationIssues, setValidationIssues] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [presence, setPresence] = useState([]);
  // Wave V.5 — surfaces auto-queued next stages after an approval
  const [approvedNext, setApprovedNext] = useState(null);
  // CH.3 — collapsible stage header (description + relationships + standards).
  // Stored in sessionStorage so it persists across stage switches.
  const [headerCollapsed, setHeaderCollapsed] = useState(() => {
    // CH.3 — default to collapsed for a focused first-load.
    try { return sessionStorage.getItem("rachana.stageHeaderCollapsed") !== "0"; }
    catch { return true; }
  });
  // Wave BJ.3 — design-case awareness for the B.1 "Update Document" button
  const [designCase, setDesignCase] = useState(null); // {run_id, name} or null
  const [updatingDoc, setUpdatingDoc] = useState(false);
  // Generic sub-tabs system. Stages with extra views (B.1 sim, B.2 PFD,
  // B.3 P&ID, B.4 datasheets, B.6 haz/qra…) get a tab strip in their
  // main panel — "Documents" is always tab 0, custom views append after.
  const [subTab, setSubTab] = useState("documents");
  // Reset sub-tab to documents whenever the active stage changes
  useEffect(() => {
    // Wave V.6 — when switching to an L3 worksheet stage, auto-open the
    // worksheet tab so the engineer lands on the tool, not blank Documents.
    // Always land on "documents" first. The user kept reporting "nothing
    // produced" because previously some leaf stages defaulted to an
    // empty viewer/editor tab even when 4+ deliverables existed. Defaulting
    // to Documents reveals the actual output immediately; the user can
    // switch to the viewer/editor tab when ready.
    setSubTab("documents");
  }, [stage.code]);

  // Wave BJ.3 — poll design-case state for B.1 so we can enable / disable the
  // "Update Document" button. Cheap GET; refetches on stage change and on
  // deliverable refresh tick.
  useEffect(() => {
    if (stage.code !== "B.1") {
      setDesignCase(null);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/projects/${projectId}/simulation/cases`);
        if (!alive) return;
        const approvedId = r.data?.approved_design_case;
        const cases = r.data?.cases || [];
        const target = approvedId ? cases.find((c) => c.run_id === approvedId) : null;
        setDesignCase(target ? { run_id: target.run_id, name: target.name || target.run_id } : null);
      } catch {
        if (alive) setDesignCase(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [stage.code, projectId, pollTick]);
  // Extra fetch for B.1 stage: check whether the sim-import path has
  // already completed this stage (so we can show a clear "you're done"
  // banner instead of the run-anyway UX).
  const [b1ImportStatus, setB1ImportStatus] = useState(null);
  useEffect(() => {
    if (stage.code !== "B.1") { setB1ImportStatus(null); return; }
    let alive = true;
    (async () => {
      try {
        const r = await api.get(
          `/projects/${projectId}/simulation/import/provenance`);
        if (alive) setB1ImportStatus(r.data);
      } catch { if (alive) setB1ImportStatus(null); }
    })();
    return () => { alive = false; };
  }, [stage.code, projectId, pollTick]);
  const fileInputRef = useRef(null);
  const apiRoot = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

  // Refresh deps + deliverables whenever stage changes
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [d, deliv, atts, cmts, val] = await Promise.all([
          api.get(`/projects/${projectId}/stages/${stage.code}/deps`),
          api.get(`/projects/${projectId}/stages/${stage.code}/deliverables`),
          api.get(`/projects/${projectId}/stages/${stage.code}/attachments`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/stages/${stage.code}/comments`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/stages/${stage.code}/validation`).catch(() => ({ data: { issues: [] } })),
        ]);
        if (alive) {
          setDeps(d.data);
          setDeliverables(deliv.data);
          // Phase 19n: preserve the user's currently-selected deliverable
          // across refetches (caused by SSE pollTick). Otherwise every
          // status update would reset the right pane back to the first
          // deliverable — which the user perceived as the page "jumping
          // to B.2.1 when they clicked B.2.2".
          setOpenDeliv((prev) => {
            if (prev) {
              const updated = deliv.data.find((x) => x.id === prev.id || x.code === prev.code);
              if (updated) return updated; // keep selection, refresh content
            }
            return deliv.data[0] || null;
          });
          setAttachments(atts.data);
          setComments(cmts.data);
          setValidationIssues(val.data.issues || []);
        }
      } catch (e) {
        if (alive) setErr(formatErr(e.response?.data?.detail) || e.message);
      }
    })();
    return () => { alive = false; };
  }, [projectId, stage.code, pollTick]);

  // Wave CB — refresh deliverables when an AI generation completes
  // (the AiGenerateButton dispatches "deliverable-ai-done" on success).
  useEffect(() => {
    const handler = () => setPollTick((t) => t + 1);
    window.addEventListener("deliverable-ai-done", handler);
    return () => window.removeEventListener("deliverable-ai-done", handler);
  }, []);

  // SSE streaming of stage status (replaces 4-s polling). Fallback to polling
  // if EventSource fails (e.g. older browsers / proxies that buffer SSE).
  //
  // ── Why this effect is carefully guarded ──────────────────────────────
  // Prior to Wave V.6.2, this effect had three compounding defects that,
  // under real enterprise usage (long-running Capexara Process Industry AI jobs + stage
  // navigation + parent re-renders), exhausted Chromium's 255-sockets-per-
  // origin pool within minutes and produced the user-visible cascade
  // "WebSocket … failed: Insufficient resources" followed by a HTTP 403
  // banner (because the browser could no longer open a TCP connection at
  // all). The three defects were:
  //   1. `onclose` unconditionally scheduled a reconnect regardless of
  //      close code, so policy-violation closes (4401/4403/4404) looped
  //      forever at exponential backoff, never giving up.
  //   2. No retry cap — a permanently-broken server produced ∞ reconnects.
  //   3. No `isMounted` guard — orphaned reconnect timers fired after the
  //      component unmounted, opening sockets onto a dead React tree.
  // Fixes below: per-effect `isMounted` flag, honour 4xxx close codes, cap
  // at 10 retries, reset the retry counter on successful `onopen`.
  useEffect(() => {
    let es = null;
    let pollId = null;
    let ws = null;
    let isMounted = true;
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    const wsBase = ((process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws")) + "/api";
    const startPolling = () => {
      if (pollId || !isMounted) return;
      pollId = setInterval(async () => {
        if (!isMounted) return;
        try {
          const { data } = await api.get(`/projects/${projectId}/stages/${stage.code}/last-run`);
          if (data.status === "complete" || data.status === "blocked") {
            setRunning(false);
            setPollTick((t) => t + 1);
            onChange?.();
          }
        } catch { /* ignore */ }
      }, 4000);
    };
    try {
      es = new EventSource(`${apiBase}/projects/${projectId}/stages/${stage.code}/stream`, { withCredentials: true });
      es.addEventListener("status", (evt) => {
        if (!isMounted) return;
        try {
          const data = JSON.parse(evt.data);
          if (data.status === "complete" || data.status === "blocked" || data.status === "approved") {
            setRunning(false);
            setPollTick((t) => t + 1);
            onChange?.();
          } else if (data.status === "in_progress") {
            setRunning(true);
            // Wave BI — SSE `status` event can include `progress`; keep it
            // in sync with polling for responsiveness.
            if (data.progress) setProgress(data.progress);
          }
        } catch { /* ignore */ }
      });
      es.onerror = () => {
        if (es) { es.close(); es = null; }
        startPolling();
      };
    } catch {
      startPolling();
    }
    // Multi-user presence WebSocket — hardened reconnect with all three
    // guards described above.
    let wsBackoff = 1500;
    let wsReconnectTimer = null;
    let wsRetryCount = 0;
    const WS_MAX_RETRIES = 10;
    const openWs = () => {
      if (!isMounted || wsRetryCount >= WS_MAX_RETRIES) return;
      try {
        ws = new WebSocket(`${wsBase}/ws/projects/${projectId}/stages/${stage.code}`);
        ws.onopen = () => {
          wsBackoff = 1500;
          wsRetryCount = 0;  // reset on any success so a long session heals naturally
        };
        ws.onmessage = (evt) => {
          if (!isMounted) return;
          try {
            const obj = JSON.parse(evt.data);
            if (obj.type === "presence") {
              setPresence(obj.users || []);
            } else if (obj.type === "stage_updated") {
              setPollTick((t) => t + 1);
            }
          } catch { /* ignore */ }
        };
        ws.onclose = (evt) => {
          // Hard stops that must NOT reconnect — unmounted, or server sent
          // a policy-violation close code (auth/authz failure). Reconnecting
          // on a 403 is what produces the user-visible storm.
          if (!isMounted) return;
          const code = evt?.code;
          if (code === 4401 || code === 4403 || code === 4404 || code === 1008) return;
          if (wsReconnectTimer) return;
          wsRetryCount += 1;
          if (wsRetryCount > WS_MAX_RETRIES) return;
          wsReconnectTimer = setTimeout(() => {
            wsReconnectTimer = null;
            wsBackoff = Math.min(wsBackoff * 2, 30000);
            openWs();
          }, wsBackoff);
        };
      } catch { /* ignore */ }
    };
    openWs();
    return () => {
      // Set isMounted FIRST so any in-flight timer callbacks no-op when
      // they eventually fire. Then tear down sockets + timers + polls.
      isMounted = false;
      if (es) es.close();
      if (pollId) clearInterval(pollId);
      if (ws) try { ws.close(); } catch { /* ignore */ }
      if (wsReconnectTimer) clearTimeout(wsReconnectTimer);
    };
  }, [projectId, stage.code, onChange]);

  // Wave BI — poll /stages/active every 2s while running to pull granular
  // progress (percent / current_step / total_steps / message).
  useEffect(() => {
    if (!running) {
      setProgress(null);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/stages/active`);
        if (cancelled) return;
        const mine = (data?.stages || []).find(
          (r) => r.stage_code === stage.code
        );
        setProgress(mine?.progress || null);
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [projectId, stage.code, running]);

  const startRun = async (promptOverride) => {
    setErr(""); setRunning(true);
    // Wave Y — defensive guard. If a button's onClick passes a SyntheticEvent
    // through here, it would carry circular Fiber refs and crash JSON.stringify.
    const overrideIsString = typeof promptOverride === "string";
    const usedPrompt = overrideIsString ? promptOverride : prompt;
    try {
      const { data } = await api.post(`/projects/${projectId}/stages/${stage.code}/run`, { prompt: usedPrompt });
      if (data.status === "blocked") {
        setRunning(false);
        setErr(`Blocked: requires ${data.deps?.hard_blockers?.join(", ") || "prerequisites"}`);
      }
    } catch (e) {
      setRunning(false);
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const decide = async (action) => {
    setBusyApprove(true);
    try {
      const { data: approveData } = await api.patch(`/projects/${projectId}/stages/${stage.code}/approve`, { action });
      // Wave V.5 — show which downstream stages are now unblocked
      if (action === "approve" && approveData?.next_unblocked?.length > 0) {
        const codes = approveData.next_unblocked.map((s) => s.code).join(", ");
        setErr("");  // clear any prior banner
        // Friendly inline success message; uses the same pf-sev-ok style
        // as elsewhere. Clears on next stage change (StagePanel re-mounts).
        setApprovedNext({
          approved: stage.code,
          next: approveData.next_unblocked,
          message: `Approved ${stage.code}. Unblocked: ${codes}.`,
        });
      }
      onChange?.();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusyApprove(false); }
  };

  const isRunning = running || stage.status === "in_progress";
  const canRun = deps?.can_run !== false || (deps?.hard_blockers || []).length === 0;

  return (
    <div data-testid={`stage-panel-${stage.code}`} style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: headerCollapsed ? "8px 20px" : "16px 20px",
                          borderBottom: "1px solid var(--pf-border)",
                          background: "var(--pf-surface-2)",
                          transition: "padding 0.18s ease" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "var(--pf-spark)", padding: "4px 8px", border: "1px solid var(--pf-spark)", borderRadius: 2 }}>
            {stage.code}
          </span>
          <h2 className="pf-heading"
              style={{ fontSize: headerCollapsed ? 16 : 22,
                          margin: 0, flex: 1,
                          transition: "font-size 0.18s ease" }}>
            {stage.name}
          </h2>
          <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.06em", textTransform: "uppercase", color: STATUS_COLOR[stage.status] || STATUS_COLOR.not_started }} data-testid={`stage-status-${stage.code}`}>
            {(stage.status || "not_started").replace("_", " ")}
          </span>
          <button type="button"
              data-testid={`stage-header-toggle-${stage.code}`}
              onClick={() => setHeaderCollapsed((v) => {
                const n = !v;
                try { sessionStorage.setItem("rachana.stageHeaderCollapsed", n ? "1" : "0"); } catch { /* ignored */ }
                return n;
              })}
              title={headerCollapsed ? "Expand stage details" : "Collapse stage details"}
              style={{ background: "transparent", border: "1px solid var(--pf-border)",
                            color: "var(--pf-text-muted)", cursor: "pointer",
                            padding: "4px 8px", borderRadius: 4,
                            display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
            {headerCollapsed ? <ChevronDown size={13}/> : <ChevronUp size={13}/>}
            {headerCollapsed ? "Details" : "Hide"}
          </button>
        </div>
        {!headerCollapsed && (
          <>
        {stage.description && <p style={{ color: "var(--pf-text-muted)", fontSize: 13, lineHeight: 1.5, margin: "10px 0 0" }}>{stage.description}</p>}
        {(stage.hard_deps?.length > 0 || stage.soft_deps?.length > 0) && (
          <DependencyExplainer
            stageCode={stage.code}
            hardDeps={stage.hard_deps || []}
            softDeps={stage.soft_deps || []}
            depReasons={stage.dep_reasons || {}}
          />
        )}
        {/* Wave Y — Stage Gate ↔ Views Panel relationships */}
        <StageRelationshipsPanel
          stageCode={stage.code}
          hardDeps={stage.hard_deps || []}
          softDeps={stage.soft_deps || []}
          stageSuccessors={deps?.stage_successors || []}
          viewsPredecessors={deps?.views_predecessors || []}
          viewsSuccessors={deps?.views_successors || []}
          onOpenStage={(code) => {
            try {
              window.dispatchEvent(new CustomEvent("rachana.stage.activate", { detail: { code } }));
            } catch { /* ignored */ }
          }}
          onOpenView={(tabKey) => onOpenTab?.(tabKey)}
          defaultOpen={false}
        />
        {presence.length > 1 && (
          <div data-testid={`stage-presence-${stage.code}`} style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--pf-text-muted)" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--pf-ok)", display: "inline-block" }} />
            <span>{presence.length} active —</span>
            {presence.slice(0, 5).map((u, i) => (
              <span key={u.user_id || i} title={u.user_name} style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 22, height: 22, borderRadius: "50%", background: "var(--pf-spark)",
                color: "#fff", fontSize: 10, fontWeight: 700,
              }}>
                {(u.user_name || "?").charAt(0).toUpperCase()}
              </span>
            ))}
            {presence.length > 5 && <span>+{presence.length - 5}</span>}
          </div>
        )}
        <StageStandardsBar projectId={projectId} stageCode={stage.code} />

        {/* Phase 19m — cross-link banner for stages that publish drawings
            or computational results to a dedicated top-tab. The deliverables
            on this stage are documentation only; the interactive view lives
            in the linked tab. */}
        {onOpenTab && (() => {
          const DRAWING_TABS = {
            "J":   { tab: "model", label: "3D Model tab", desc: "Plant 3D model & close-out" },
          };
          const info = DRAWING_TABS[stage.code];
          if (!info) return null;
          return (
            <div
              style={{
                marginTop: 12,
                padding: "10px 14px",
                border: "1px solid var(--pf-spark)",
                borderRadius: 6,
                background: "color-mix(in srgb, var(--pf-spark) 8%, transparent)",
                display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              }}
              data-testid={`stage-drawing-link-${stage.code}`}
            >
              <div style={{ flex: 1, minWidth: 220 }}>
                <div className="pf-overline" style={{ color: "var(--pf-spark)", marginBottom: 2 }}>RESULT PUBLISHED TO DEDICATED TAB</div>
                <div style={{ fontSize: 13, color: "var(--pf-text)" }}>
                  This stage produces <strong>{info.desc}</strong>. Documentation appears below — the interactive view opens in the <strong>{info.label}</strong>.
                </div>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => {
                  // Phase 19n: remember origin so the tab can show a "Back to stage" button
                  try { sessionStorage.setItem(`rachana.tab.${info.tab}.from`, stage.code); } catch { /* ignored */ }
                  onOpenTab(info.tab);
                }}
                data-testid={`stage-open-${info.tab}-tab`}
                style={{ fontWeight: 600 }}
              >
                Open {info.label} →
              </button>
            </div>
          );
        })()}
        </>
        )}
      </div>

      {/* Stage-specific sub-tabs strip. "Documents" is always present; stages
          like B.1/B.2/B.3/B.4/B.6 also expose their interactive views (Process
          Simulation, PFD, P&ID, Datasheets, Hazardous Area, QRA) right here
          instead of in the global views nav. */}
      {(() => {
        const docsTab = { key: "documents", label: "Documents", Icon: FileText };
        const built = [];
        if (stage.code === "B.1") {
          // Stage B Final Pass — External simulator import comes first
          // so users can skip re-running the internal solver by uploading
          // their HYSYS / Aspen / PRO-II / etc. case output.
          built.push({
            key: "sim-import",
            label: "Import HYSYS / Aspen",
            Icon: Upload,
            render: () => <SimulationImportPanel projectId={projectId} />,
          });
          built.push({
            key: "simulation",
            label: "Process Simulation",
            Icon: Sparkles,
            render: () => <SimulationViewer projectId={projectId} />,
          });
        }
        // Wave V.6 / Wave X — Interactive worksheets for L3 / L2 leaves
        if (stage.code === "B.6.1") built.push({ key: "hazop", label: "HAZOP Worksheet", Icon: Shield, render: () => <HazopWorksheet projectId={projectId} /> });
        if (stage.code === "B.6.2") built.push({ key: "sil", label: "SIL / LOPA", Icon: ShieldCheck, render: () => <SilLopaWorksheet projectId={projectId} /> });
        if (stage.code === "I.2")   built.push({ key: "tbe", label: "TBE Matrix", Icon: FileSpreadsheet, render: () => <TbeWorksheet projectId={projectId} /> });
        if (stage.code === "F.4")   built.push({ key: "loops", label: "Loop Diagrams", Icon: FileSignature, render: () => <LoopDiagramsPanel projectId={projectId} /> });
        if (stage.code === "H.1")   built.push({ key: "fire-water", label: "Fire Water Sizing", Icon: Sparkles, render: () => <FireWaterPanel projectId={projectId} /> });
        // Wave AH+AI — Editor sub-tab for every stage that has a palette/editor per spec §6.1
        // Wave CC — PFD / P&ID editor tabs (user issues #2 + #3)
        if (stage.code === "B.2")   {
          built.push({ key: "pfd-editor", label: "PFD Editor", Icon: FileSignature, render: () => <PfdDocumentFirstPanel projectId={projectId} /> });
          // Stage B Final Pass — corporate drawing-template management
          // for PFD + P&ID output (title block, borders, revision block).
          built.push({
            key: "company-template",
            label: "Company Template",
            Icon: FileImage,
            render: () => <DrawingTemplatePanel projectId={projectId} defaultApplyTo="pfd" />,
          });
        }
        if (stage.code === "B.3")   {
          built.push({ key: "pid-editor", label: "P&ID Editor", Icon: FileSignature, render: () => <PidDocumentRegister projectId={projectId} /> });
          // Stage B Final Pass — client template drives P&ID output too.
          built.push({
            key: "company-template",
            label: "Company Template",
            Icon: FileImage,
            render: () => <DrawingTemplatePanel projectId={projectId} defaultApplyTo="pid" />,
          });
        }
        // Stage C · Mechanical Engineering (C.1-C.5 all backed by StageCPanel)
        if (["C.1", "C.2", "C.3", "C.4", "C.5"].includes(stage.code)) {
          built.push({
            key: "mech-eng",
            label: "Mechanical Engineering",
            Icon: FileSignature,
            render: () => <StageCPanel projectId={projectId} subStage={stage.code} />,
          });
        }
        // Stage D · Layout & Piping (D.1-D.7 all backed by StageDPanel)
        if (["D.1", "D.2", "D.3", "D.4", "D.5", "D.6", "D.7"].includes(stage.code)) {
          built.push({
            key: "layout-piping",
            label: "Layout & Piping",
            Icon: FileText,
            render: () => <StageDPanel projectId={projectId} subStage={stage.code} />,
          });
        }
        // Stage E · Electrical Engineering (E.1-E.6 all backed by StageEPanel)
        // Catalog numbering: E.1 = Load & Power, E.2 = Haz-area, E.3 = SLD, E.4 = Earthing,
        // E.5 = Cable, E.6 = Electrical MTO — see PROMPT_STAGE_E_FIXES.md.
        if (["E.1", "E.2", "E.3", "E.4", "E.5", "E.6"].includes(stage.code)) {
          built.push({
            key: "electrical",
            label: "Electrical Engineering",
            Icon: FileText,
            render: () => <StageEPanel projectId={projectId} subStage={stage.code} />,
          });
        }
        // Stage F · Instrumentation & Control (F.1-F.6 all backed by StageFPanel)
        // Catalog: F.1 Architecture, F.2 Instrument Index, F.3 C&E+Logic,
        // F.4 Loop Diagrams, F.5 Hookups, F.6 Instrument MTO — see PROMPT_STAGE_F_FIXES.md.
        if (["F.1", "F.2", "F.3", "F.4", "F.5", "F.6"].includes(stage.code)) {
          built.push({
            key: "instrumentation",
            label: "Instrumentation & Control",
            Icon: FileSignature,
            render: () => <StageFPanel projectId={projectId} subStage={stage.code} />,
          });
        }
        // Stage G · Civil & Structural (G.1-G.5 backed by StageGPanel)
        if (["G.1", "G.2", "G.3", "G.4", "G.5"].includes(stage.code)) {
          built.push({
            key: "civil",
            label: "Civil & Structural",
            Icon: FileText,
            render: () => <StageGPanel projectId={projectId} subStage={stage.code} />,
          });
        }
        // Stage H · Fire Protection (H.1-H.4 backed by StageHPanel — 25 fixes)
        if (["H.1", "H.2", "H.3", "H.4"].includes(stage.code)) {
          built.push({
            key: "fire",
            label: "Fire Protection",
            Icon: Shield,
            render: () => <StageHPanel projectId={projectId} subStage={stage.code} />,
          });
        }
        // Stage I · Procurement Support (I.1-I.4 backed by StageIPanel — 25 fixes)
        if (["I.1", "I.2", "I.3", "I.4"].includes(stage.code)) {
          built.push({
            key: "procurement",
            label: "Procurement",
            Icon: FileText,
            render: () => <StageIPanel projectId={projectId} subStage={stage.code} />,
          });
        }
        // Stage J · 3D Model & Close-Out (J.1-J.4 backed by StageJPanel — 26 fixes)
        if (["J.1", "J.2", "J.3", "J.4"].includes(stage.code)) {
          built.push({
            key: "closeout",
            label: "3D Model & Close-Out",
            Icon: FileText,
            render: () => <StageJPanel projectId={projectId} subStage={stage.code} />,
          });
        }
        // NOTE: D.1 = Pipe Classes (per stages_catalog.py). Plot Plan Editor
        // lives on D.2 alongside ProcessPlantD2View — moved here Feb 2026.
        if (stage.code === "D.2")   built.push({ key: "plot-plan", label: "Plot Plan Editor", Icon: FileText, render: () => <PlotPlanEditor projectId={projectId} /> });
        // Wave CD — D.2 dedicated view (plot plan + equipment layout + pipe rack + 3D + Cesium)
        if (stage.code === "D.2")   built.push({ key: "d2-view", label: "D.2 Layout & 3D", Icon: Layers, render: () => <ProcessPlantD2View projectId={projectId} /> });
        if (stage.code === "D.3")   built.push({ key: "piping-3d", label: "3D Piping Editor", Icon: FileText, render: () => <Model3DEditor projectId={projectId} /> });
        // Wave CC — Hazardous Area Editor moved from E.1 to E.2 (user issue #11)
        // Industry convention: E.1 = Load List / Power Studies, E.2 = Hazardous Area Classification
        if (stage.code === "E.2")   built.push({ key: "hazarea-editor", label: "Haz Area Editor", Icon: Shield, render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="hazarea" /> });
        if (stage.code === "E.3")   built.push({ key: "sld-editor", label: "SLD", Icon: FileSignature, render: () => <SldDocumentRegister projectId={projectId} /> });
        if (stage.code === "E.4")   built.push({ key: "earthing-editor", label: "Earthing Layout Editor", Icon: FileText, render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="earthing" /> });
        if (stage.code === "E.5")   built.push({ key: "cable-tray-editor", label: "Cable Tray Editor", Icon: FileText, render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="cable_tray" /> });
        if (stage.code === "F.1")   built.push({ key: "control-arch-editor", label: "Control Architecture Editor", Icon: FileSignature, render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="control_arch" /> });
        if (stage.code === "F.2")   built.push({ key: "instrument-editor", label: "Instrument Index Editor", Icon: FileSignature, render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="instrument" /> });
        if (stage.code === "F.3")   built.push({ key: "ce-matrix", label: "C&E Matrix Editor", Icon: ShieldCheck, render: () => <CEMatrixEditor projectId={projectId} /> });
        if (stage.code === "F.4")   built.push({ key: "loop-editor", label: "Loop Diagram Editor", Icon: FileSignature, render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="loop_diagram" /> });
        if (stage.code === "F.5")   built.push({ key: "hook-up", label: "Hook-Up Generator", Icon: FileSignature, render: () => <HookUpPanel projectId={projectId} /> });
        if (stage.code === "G.2")   built.push({ key: "foundation-editor", label: "Foundation Layout Editor", Icon: FileText, render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="foundation" /> });
        if (stage.code === "G.3")   built.push({ key: "steel-editor", label: "Steel GA Editor", Icon: FileText, render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="steel_ga" /> });
        if (stage.code === "G.4")   built.push({ key: "rc-editor", label: "RC Detail Editor", Icon: FileText, render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="rc_detail" /> });
        if (stage.code === "H.1")   built.push({ key: "fire-water-layout", label: "Fire Water Layout", Icon: FileText, render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="fire_water" /> });
        if (stage.code === "H.2")   built.push({ key: "detection-editor", label: "Detection Layout Editor", Icon: FileText, render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="detection" /> });
        if (stage.code === "H.3")   built.push({ key: "suppression-editor", label: "Suppression Layout Editor", Icon: FileText, render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="suppression" /> });
        // Wave CF — J.1 Master Federated 3D Model (rolls up D.2 plot plan, A.6 equipment, D.3 piping)
        if (stage.code === "J.1")   built.push({ key: "j1-master-3d", label: "Master 3D Model", Icon: Layers, render: () => <J1FederatedModelView projectId={projectId} /> });
        if (stage.code === "A.6") {
          if (domain === "process_plant") {
            built.push({ key: "equipment-layout", label: "Equipment Layout", Icon: Grid3X3, render: () => <ProcessPlantA6View projectId={projectId} /> });
          } else {
            built.push({ key: "equipment-layout", label: "Equipment Layout", Icon: Grid3X3, render: () => <EquipmentLayoutEditor projectId={projectId} onRerun={() => setPollTick((t) => t + 1)} /> });
          }
        }
        // Wave CO — Universal Drawings tab for stages C → J (and their
        // L2 sub-stages). Mirrors the B.2 / B.3 register UX so every
        // CAD-style SVG produced by the stage shows up as its own
        // row with View / Edit jumps.
        if (DRAWINGS_TAB_REGEX.test(stage.code || "")) {
           const editorKey = DRAWINGS_EDITOR_JUMPS[stage.code] || null;
           built.push({
              key: "drawings",
              label: "Drawings",
              Icon: FileSpreadsheet,
              render: () => (
                 <StageDrawingsRegister
                    projectId={projectId}
                    stageCode={stage.code}
                    stageName={stage.name}
                    editorTabLabel={editorKey ? "editor" : null}
                    onOpenEditor={editorKey
                       ? () => setSubTab(editorKey)
                       : null} />
              ),
           });
        }
        const fromParent = (extraTabs && extraTabs[stage.code]) || [];
        const allSubTabs = [docsTab, ...built, ...fromParent];
        if (allSubTabs.length <= 1) return null;
        return (
          <div
            data-testid={`stage-subtabs-${stage.code}`}
            style={{
              display: "flex", gap: 0, borderBottom: "1px solid var(--pf-border)",
              background: "var(--pf-surface)", paddingLeft: 20, flexWrap: "wrap",
            }}
          >
            {allSubTabs.map((tb) => {
              const active = subTab === tb.key;
              const Ic = tb.Icon || FileText;
              return (
                <button
                  key={tb.key}
                  type="button"
                  onClick={() => setSubTab(tb.key)}
                  data-testid={`stage-subtab-${stage.code}-${tb.key}`}
                  style={{
                    background: "transparent", border: "none",
                    padding: "10px 16px", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 8,
                    color: active ? "var(--pf-text)" : "var(--pf-text-muted)",
                    borderBottom: active ? "2px solid var(--pf-spark)" : "2px solid transparent",
                    fontSize: 13, fontWeight: active ? 600 : 400,
                  }}
                >
                  <Ic size={14} color={active ? "var(--pf-spark)" : "var(--pf-text-dim)"} />
                  {tb.label}
                </button>
              );
            })}
          </div>
        );
      })()}

      {(() => {
        // Render either the documents body (default) or the active sub-tab content.
        const docsTab = { key: "documents" };
        const built = stage.code === "B.1"
            ? [
                { key: "sim-import", render: () => <SimulationImportPanel projectId={projectId} /> },
                { key: "simulation", render: () => <SimulationViewer projectId={projectId} /> },
              ]
            : [];
        if (stage.code === "B.6.1") built.push({ key: "hazop", render: () => <HazopWorksheet projectId={projectId} /> });
        if (stage.code === "B.6.2") built.push({ key: "sil", render: () => <SilLopaWorksheet projectId={projectId} /> });
        if (stage.code === "I.2")   built.push({ key: "tbe", render: () => <TbeWorksheet projectId={projectId} /> });
        if (stage.code === "F.4")   built.push({ key: "loops", render: () => <LoopDiagramsPanel projectId={projectId} /> });
        if (stage.code === "H.1")   built.push({ key: "fire-water", render: () => <FireWaterPanel projectId={projectId} /> });
        if (stage.code === "B.2")   {
          built.push({ key: "pfd-editor", render: () => <PfdDocumentFirstPanel projectId={projectId} /> });
          built.push({ key: "company-template", render: () => <DrawingTemplatePanel projectId={projectId} defaultApplyTo="pfd" /> });
        }
        if (stage.code === "B.3")   {
          built.push({ key: "pid-editor", render: () => <PidDocumentRegister projectId={projectId} /> });
          built.push({ key: "company-template", render: () => <DrawingTemplatePanel projectId={projectId} defaultApplyTo="pid" /> });
        }
        if (["C.1", "C.2", "C.3", "C.4", "C.5"].includes(stage.code)) {
          built.push({ key: "mech-eng", render: () => <StageCPanel projectId={projectId} subStage={stage.code} /> });
        }
        if (["D.1", "D.2", "D.3", "D.4", "D.5", "D.6", "D.7"].includes(stage.code)) {
          built.push({ key: "layout-piping", render: () => <StageDPanel projectId={projectId} subStage={stage.code} /> });
        }
        if (["E.1", "E.2", "E.3", "E.4", "E.5", "E.6"].includes(stage.code)) {
          built.push({ key: "electrical", render: () => <StageEPanel projectId={projectId} subStage={stage.code} /> });
        }
        if (["F.1", "F.2", "F.3", "F.4", "F.5", "F.6"].includes(stage.code)) {
          built.push({ key: "instrumentation", render: () => <StageFPanel projectId={projectId} subStage={stage.code} /> });
        }
        if (["G.1", "G.2", "G.3", "G.4", "G.5"].includes(stage.code)) {
          built.push({ key: "civil", render: () => <StageGPanel projectId={projectId} subStage={stage.code} /> });
        }
        if (["H.1", "H.2", "H.3", "H.4"].includes(stage.code)) {
          built.push({ key: "fire", render: () => <StageHPanel projectId={projectId} subStage={stage.code} /> });
        }
        if (["I.1", "I.2", "I.3", "I.4"].includes(stage.code)) {
          built.push({ key: "procurement", render: () => <StageIPanel projectId={projectId} subStage={stage.code} /> });
        }
        if (["J.1", "J.2", "J.3", "J.4"].includes(stage.code)) {
          built.push({ key: "closeout", render: () => <StageJPanel projectId={projectId} subStage={stage.code} /> });
        }
        // NOTE: PlotPlanEditor lives on D.2 (Plot Plan) per stages_catalog.py.
        // Historical D.1 mapping was removed Feb 2026 (D.1 = Pipe Classes).
        if (stage.code === "D.2")   built.push({ key: "plot-plan", render: () => <PlotPlanEditor projectId={projectId} /> });
        if (stage.code === "D.2")   built.push({ key: "d2-view", render: () => <ProcessPlantD2View projectId={projectId} /> });
        if (stage.code === "D.3")   built.push({ key: "piping-3d", render: () => <Model3DEditor projectId={projectId} /> });
        if (stage.code === "E.2")   built.push({ key: "hazarea-editor", render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="hazarea" /> });
        if (stage.code === "E.3")   built.push({ key: "sld-editor", render: () => <SldDocumentRegister projectId={projectId} /> });
        if (stage.code === "E.4")   built.push({ key: "earthing-editor", render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="earthing" /> });
        if (stage.code === "E.5")   built.push({ key: "cable-tray-editor", render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="cable_tray" /> });
        if (stage.code === "F.1")   built.push({ key: "control-arch-editor", render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="control_arch" /> });
        if (stage.code === "F.3")   built.push({ key: "ce-matrix", render: () => <CEMatrixEditor projectId={projectId} /> });
        if (stage.code === "F.5")   built.push({ key: "hook-up", render: () => <HookUpPanel projectId={projectId} /> });
        if (stage.code === "G.2")   built.push({ key: "foundation-editor", render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="foundation" /> });
        if (stage.code === "G.3")   built.push({ key: "steel-editor", render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="steel_ga" /> });
        if (stage.code === "G.4")   built.push({ key: "rc-editor", render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="rc_detail" /> });
        if (stage.code === "H.1")   built.push({ key: "fire-water-layout", render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="fire_water" /> });
        if (stage.code === "H.2")   built.push({ key: "detection-editor", render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="detection" /> });
        if (stage.code === "H.3")   built.push({ key: "suppression-editor", render: () => <DisciplineCanvasEditor projectId={projectId} disciplineKey="suppression" /> });
        if (stage.code === "J.1")   built.push({ key: "j1-master-3d", render: () => <J1FederatedModelView projectId={projectId} /> });
        if (stage.code === "A.6") {
          if (domain === "process_plant") {
            built.push({ key: "equipment-layout", render: () => <ProcessPlantA6View projectId={projectId} /> });
          } else {
            built.push({ key: "equipment-layout", render: () => <EquipmentLayoutEditor projectId={projectId} onRerun={() => setPollTick((t) => t + 1)} /> });
          }
        }
        // Wave CO — Drawings tab renderer (matches the label set above).
        if (DRAWINGS_TAB_REGEX.test(stage.code || "")) {
           const editorKey = DRAWINGS_EDITOR_JUMPS[stage.code] || null;
           built.push({
              key: "drawings",
              render: () => (
                 <StageDrawingsRegister
                    projectId={projectId}
                    stageCode={stage.code}
                    stageName={stage.name}
                    editorTabLabel={editorKey ? "editor" : null}
                    onOpenEditor={editorKey
                       ? () => setSubTab(editorKey)
                       : null} />
              ),
           });
        }
        const fromParent = (extraTabs && extraTabs[stage.code]) || [];
        const allSubTabs = [docsTab, ...built, ...fromParent];
        const active = allSubTabs.find((t) => t.key === subTab) || allSubTabs[0];
        if (active && active.key !== "documents" && active.render) {
          return (
            <div
              style={{ flex: 1, overflow: "auto", padding: 20 }}
              data-testid={`stage-subtab-pane-${stage.code}-${active.key}`}
            >
              {active.render()}
            </div>
          );
        }
        return null;
      })()}

      {(stage.code === "B.1" || stage.code === "B.2" || stage.code === "B.3"
          || stage.code === "B.4" || stage.code === "B.6"
          || stage.code === "B.6.1" || stage.code === "B.6.2"
          || stage.code === "C.1" || stage.code === "C.2" || stage.code === "C.3"
          || stage.code === "C.4" || stage.code === "C.5"
          || stage.code === "D.1" || stage.code === "D.2" || stage.code === "D.3"
          || stage.code === "D.4" || stage.code === "D.5" || stage.code === "D.6"
          || stage.code === "D.7"
          || stage.code === "I.2" || stage.code === "F.4"
          || stage.code === "D.1" || stage.code === "D.2" || stage.code === "D.3"
          || stage.code === "E.2" || stage.code === "E.3"
          || stage.code === "E.4" || stage.code === "E.5"
          || stage.code === "F.1" || stage.code === "F.3" || stage.code === "F.5"
          || stage.code === "G.2" || stage.code === "G.3" || stage.code === "G.4"
          || stage.code === "H.1" || stage.code === "H.2" || stage.code === "H.3"
          || stage.code === "H.4"
          || stage.code === "I.1" || stage.code === "I.2" || stage.code === "I.3"
          || stage.code === "I.4"
          || stage.code === "J.1" || stage.code === "J.2" || stage.code === "J.3"
          || stage.code === "J.4"
          || stage.code === "A.6"
          || /* Wave CO — universal Drawings sub-tab */
             (DRAWINGS_TAB_REGEX.test(stage.code || "")
              && subTab === "drawings")
          ) && subTab !== "documents" ? null : (
      <div data-testid={`stage-scroll-${stage.code}`}
           style={{ flex: 1, overflow: "auto", padding: 20,
                      /* CH.3 — subtle CAD dot-grid background so the
                         empty area below short content reads as an
                         intentional workspace (matches PFD/PID viewers). */
                      backgroundImage: "radial-gradient(rgba(148,163,184,0.10) 1px, transparent 1px)",
                      backgroundSize: "22px 22px",
                      backgroundAttachment: "local" }}>
        {err && (
          <div
            className="pf-sev pf-sev-error"
            style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", marginBottom: 14 }}
            data-testid={`stage-err-${stage.code}`}
          >
            <span style={{ flex: 1 }}>{err}</span>
            <button
              type="button"
              onClick={() => setErr("")}
              data-testid={`stage-err-dismiss-${stage.code}`}
              title="Dismiss"
              style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0 }}
            >×</button>
          </div>
        )}

        {/* B.1 sim-import success banner — shown whenever HYSYS/Aspen data
            has already been committed. Explicitly tells the user they
            can skip the Run button (which requires A.1 first) and jump
            straight to B.2 or work with the imported case as-is. */}
        {stage.code === "B.1" && b1ImportStatus
          && b1ImportStatus.b1_status?.status === "complete" && (
          <div className="pf-card"
               data-testid="stage-b1-import-complete"
               style={{ padding: 14, marginBottom: 16,
                          borderColor: "#22c55e",
                          background: "rgba(34,197,94,0.08)",
                          display: "flex", gap: 12,
                          alignItems: "flex-start" }}>
            <CheckCircle2 size={20} color="#22c55e"
                            style={{ marginTop: 2 }}/>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700,
                              color: "#86efac" }}>
                B.1 already complete via {b1ImportStatus.simulator?.toUpperCase() || "simulator"} import
              </div>
              <div style={{ fontSize: 12.5, color: "var(--pf-text-muted)",
                              marginTop: 4, lineHeight: 1.55 }}>
                Your file <b>{b1ImportStatus.filename || "H&MB"}</b> was committed
                on {b1ImportStatus.b1_status?.committed_at ? new Date(b1ImportStatus.b1_status.committed_at).toLocaleString() : "—"}
                {" — "}<b>{b1ImportStatus.streams_count}</b> streams,
                {" "}<b>{b1ImportStatus.equipment_count}</b> equipment persisted
                to <code>pdo.simulation</code>. Downstream stages (B.2 PFD, B.3 P&amp;ID, A.6 Layout) are unlocked and already use this data.
              </div>
              <div style={{ fontSize: 12, color: "var(--pf-text-muted)",
                              marginTop: 8, fontStyle: "italic" }}>
                The Run button below is optional — it regenerates the AI-authored B.1.1–B.1.4 narrative documents from the imported case. You can safely skip it and proceed directly to B.2.
              </div>
            </div>
          </div>
        )}

        {/* Dependency banner */}
        {deps && !deps.can_run && (
          <div className="pf-card" style={{ padding: 14, marginBottom: 16, borderColor: "var(--pf-err)", display: "flex", gap: 12, alignItems: "flex-start" }} data-testid={`stage-deps-blocker-${stage.code}`}>
            <Lock size={18} color="var(--pf-err)" style={{ marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div className="pf-heading" style={{ fontSize: 14, margin: 0, color: "var(--pf-err)" }}>
                Cannot run <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>{stage.code}</strong> yet — blocked by {deps.hard_blockers.length > 1 ? "stages" : "stage"} <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>{deps.hard_blockers.join(", ")}</strong>
              </div>
              <div style={{ fontSize: 13, color: "var(--pf-text-muted)", marginTop: 4 }}>
                Complete {deps.hard_blockers.length > 1 ? "these stages" : "this stage"} first, or click <em>Force run</em> below to proceed with placeholders.
              </div>
              {deps.dep_reasons && deps.hard_blockers.some((b) => deps.dep_reasons[b]) && (
                <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 12.5, color: "var(--pf-text-muted)", lineHeight: 1.55 }} data-testid={`stage-deps-blocker-reasons-${stage.code}`}>
                  {deps.hard_blockers.map((b) => deps.dep_reasons[b] && (
                    <li key={b} style={{ marginBottom: 4 }}>
                      <strong style={{ color: "var(--pf-err)", fontFamily: "'JetBrains Mono', monospace" }}>{b}</strong> — {deps.dep_reasons[b]}
                    </li>
                  ))}
                </ul>
              )}
              {stage.code === "B.1" && (
                <div style={{ marginTop: 10, padding: 10, background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.4)", borderRadius: 4, fontSize: 12.5, color: "#86efac" }} data-testid="stage-b1-import-hint">
                  <strong>💡 Shortcut:</strong> instead of running the Capexara AI here, you can jump to the <strong>Import HYSYS / Aspen</strong> sub-tab (above) and commit a simulator export — that completes B.1 immediately without needing {deps.hard_blockers.join(", ")} first.
                </div>
              )}
            </div>
          </div>
        )}
        {deps && deps.soft_warnings?.length > 0 && deps.can_run && (
          <div className="pf-card" style={{ padding: 12, marginBottom: 16, borderColor: "var(--pf-spark)", display: "flex", gap: 10, alignItems: "flex-start" }}>
            <AlertTriangle size={16} color="var(--pf-spark)" style={{ marginTop: 2 }} />
            <div style={{ fontSize: 12.5, color: "var(--pf-text-muted)", flex: 1 }}>
              Soft dependencies pending: <strong style={{ color: "var(--pf-spark)" }}>{deps.soft_warnings.join(", ")}</strong> — output may be lower fidelity.
              {deps.dep_reasons && deps.soft_warnings.some((b) => deps.dep_reasons[b]) && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18, lineHeight: 1.55 }} data-testid={`stage-deps-soft-reasons-${stage.code}`}>
                  {deps.soft_warnings.map((b) => deps.dep_reasons[b] && (
                    <li key={b} style={{ marginBottom: 4 }}>
                      <strong style={{ color: "var(--pf-spark)", fontFamily: "'JetBrains Mono', monospace" }}>{b}</strong> — {deps.dep_reasons[b]}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Prompt area */}
        {stage.is_leaf === false ? (
          <ParentStageRollup stage={stage} projectId={projectId}
              onPick={(code) => onChange?.(code)} />
        ) : (["A.1", "A.2", "A.3", "A.4", "A.5"].includes(stage.code) ? (
          <DesignBasisWizard projectId={projectId} stageCode={stage.code} />
        ) : (
        <>
        <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "center", marginBottom: 6 }}>
          <div className="pf-overline">{t("prompt_for_this_stage", "PROMPT FOR THIS STAGE")}</div>
          <MicButton testId={`stage-prompt-mic-${stage.code}`} size="sm"
              onTranscript={(tx) => setPrompt(prev => prev ? prev + " " + tx : tx)}
              onAutoSubmit={(tx) => { setPrompt(tx); if (!isRunning) startRun(tx); }} />
        </div>
        <textarea
          className="pf-input pf-textarea"
          rows={4}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Optional: add specific instructions or hold Space to dictate (constraints, design margins, vendor preferences, etc.)."
          disabled={isRunning}
          data-testid={`stage-prompt-${stage.code}`}
        />
        <div
          data-testid={`stage-actions-${stage.code}`}
          style={{
            display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap",
            position: "sticky", bottom: -20, zIndex: 5,
            padding: "12px 14px",
            margin: "10px -20px -20px",
            background: "var(--pf-surface)",
            borderTop: "1px solid var(--pf-border)",
            boxShadow: "0 -6px 16px rgba(0,0,0,0.25)",
            alignItems: "center",
          }}
        >
          <button className="btn btn-primary" onClick={() => startRun()} disabled={isRunning} data-testid={`stage-run-${stage.code}`}>
            {isRunning ? <><Loader2 className="pf-spin" size={14} /> Running… (up to 7 min)</> : <><Play size={14} /> {t("run_stage", "Run stage")}</>}
          </button>
          {/* Wave BJ.3 — Update Document button (B.1 only). Visible whenever a
              Design Case exists; disabled if not. Re-renders B.1.1–B.1.4 from
              the marked Design Case as a NEW revision (V2, V3, …). NO Capexara Process Industry AI
              call — uses the case's stored AI assessment. */}
          {stage.code === "B.1" && (
            <button
              className="btn btn-ghost"
              data-testid="stage-update-doc-B.1"
              disabled={!designCase || isRunning || updatingDoc}
              title={
                !designCase
                  ? "Mark a case as Design Case in the Process Simulation tab to enable this."
                  : `Re-render B.1.1–B.1.4 from "${designCase.name}" as a new revision`
              }
              onClick={async () => {
                if (!designCase) return;
                if (
                  !window.confirm(
                    `Update B.1.1–B.1.4 documents from Design Case "${designCase.name}"?\n\n` +
                      "A new revision (V2, V3, …) will be created. The current latest version becomes history.\n\n" +
                      "No Capexara Process Industry AI call — uses the stored simulation results."
                  )
                )
                  return;
                setErr("");
                setUpdatingDoc(true);
                try {
                  const r = await api.post(
                    `/projects/${projectId}/stages/B.1/update-from-design-case`,
                    {}
                  );
                  setUpdatingDoc(false);
                  // Trigger a deliverables refetch so the new version appears
                  setPollTick((t) => t + 1);
                  alert(
                    `✅ Documents updated from "${r.data?.case_name || designCase.name}".\n` +
                      `Codes refreshed: ${(r.data?.updated_codes || []).join(", ")} · ` +
                      `New version: V${(r.data?.new_versions || []).slice(-1)[0] || "?"}`
                  );
                } catch (e) {
                  setUpdatingDoc(false);
                  setErr(formatErr(e.response?.data?.detail) || "Update failed");
                }
              }}
            >
              {updatingDoc ? (
                <>
                  <Loader2 className="pf-spin" size={14} /> Updating…
                </>
              ) : (
                <>
                  <FileSignature size={14} /> Update document
                  {designCase ? ` (from ${designCase.name})` : ""}
                </>
              )}
            </button>
          )}
          {!canRun && (
            <button className="btn btn-ghost" onClick={async () => { setErr(""); setRunning(true); try { await api.post(`/projects/${projectId}/stages/${stage.code}/run`, { prompt, force: true }); } catch (e) { setRunning(false); setErr(formatErr(e.response?.data?.detail)); } }} disabled={isRunning} data-testid={`stage-force-${stage.code}`}>
              Force run with placeholders
            </button>
          )}
          {isRunning && (
            <button
              className="btn btn-danger"
              onClick={async () => {
                if (!window.confirm("Cancel this stage run? The pending AI call may still be billed before it terminates.")) return;
                try {
                  await api.post(`/projects/${projectId}/stages/${stage.code}/cancel`);
                  setRunning(false);
                  setErr("Stage run cancelled. You can retry now.");
                } catch (e) {
                  setErr(formatErr(e.response?.data?.detail) || "Could not cancel run");
                }
              }}
              data-testid={`stage-cancel-${stage.code}`}
            >
              <X size={14} /> Cancel run
            </button>
          )}
        </div>
        {isRunning && (
          <div className="pf-mono" style={{ fontSize: 11, color: "var(--pf-text-muted)", marginTop: 8 }}>
            Generating engineering deliverables via Capexara Process Industry AI · single attempt · timeout 7 min · this is normal — long structured documents take time to author. You may safely navigate to other tabs; the run continues in the background.
          </div>
        )}

        {/* Wave BI — Granular stage progress (percent + step count + message).
            Visible on EVERY stage gate whenever the backend reports sub-step
            progress. Simulations emit per-section updates; other specialists
            default to "Preparing → Calling AI → Saving". */}
        {isRunning && progress && (
          <div
            data-testid={`stage-progress-${stage.code}`}
            className="pf-card"
            style={{
              marginTop: 10, padding: "10px 12px",
              background: "rgba(34,211,238,0.06)",
              border: "1px solid rgba(34,211,238,0.3)",
              borderRadius: 8,
            }}
          >
            <div style={{
              display: "flex", justifyContent: "space-between",
              alignItems: "center", gap: 10, marginBottom: 6,
            }}>
              <div style={{ fontSize: 12.5, color: "var(--pf-text)",
                              flex: 1, minWidth: 0 }}
                   data-testid={`stage-progress-message-${stage.code}`}>
                {progress.message || "Working…"}
              </div>
              {progress.percent != null && (
                <div
                  data-testid={`stage-progress-percent-${stage.code}`}
                  className="pf-mono"
                  style={{ fontSize: 13, fontWeight: 700,
                            color: "var(--pf-spark, #22d3ee)" }}
                >
                  {Math.round(progress.percent)}%
                </div>
              )}
            </div>
            <div style={{
              height: 6, background: "rgba(255,255,255,0.08)",
              borderRadius: 3, overflow: "hidden",
            }}>
              <div
                data-testid={`stage-progress-bar-${stage.code}`}
                style={{
                  width: `${Math.max(2, Math.min(100, progress.percent || 0))}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, #22d3ee, #0ea5e9)",
                  transition: "width 500ms ease",
                  borderRadius: 3,
                }}
              />
            </div>
            {progress.current_step != null && progress.total_steps != null && (
              <div className="pf-mono" style={{
                fontSize: 10.5, color: "var(--pf-text-muted)",
                marginTop: 4, letterSpacing: "0.03em",
              }}>
                step {progress.current_step} of {progress.total_steps}
                {progress.label && ` · ${progress.label}`}
              </div>
            )}
          </div>
        )}

        {/* Phase 19o — Stage Handover Bundle ZIP. Available once the stage
            has at least one deliverable. Bundles cover sheet + every
            deliverable as MD / SVG / PDF / Word / Excel. */}
        {deliverables.length > 0 && (
          <div className="pf-card" style={{ padding: 12, marginTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }} data-testid={`stage-handover-${stage.code}`}>
            <div>
              <div className="pf-overline" style={{ marginBottom: 2 }}>STAGE HANDOVER BUNDLE</div>
              <div style={{ fontSize: 12.5, color: "var(--pf-text-muted)" }}>
                Download a single ZIP with every deliverable on this stage as Markdown · SVG · PDF · Word · Excel — ready to send to clients or auditors.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <a
                className="btn btn-primary"
                href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/projects/${projectId}/stages/${stage.code}/handover.zip`}
                data-testid={`stage-handover-zip-${stage.code}`}
                style={{ fontWeight: 600 }}
              >
                <Download size={14} /> Download .zip
              </a>
              {onSendHandover && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => onSendHandover({
                    scope: "stage",
                    stageCode: stage.code,
                    label: `Stage ${stage.code} — ${stage.name || ""} handover`,
                  })}
                  data-testid={`stage-send-${stage.code}`}
                  style={{ fontWeight: 600 }}
                >
                  <Send size={14} /> Send to recipient
                </button>
              )}
            </div>
          </div>
        )}

        {/* Approval section */}
        {approvalGatesEnabled && stage.status === "complete" && (
          <div className="pf-card" style={{ padding: 14, marginTop: 18, borderColor: "var(--pf-spark)" }} data-testid={`stage-approval-${stage.code}`}>
            <div className="pf-overline" style={{ marginBottom: 8 }}>APPROVAL · STAGE GATE</div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-primary" onClick={() => decide("approve")} disabled={busyApprove} data-testid={`stage-approve-${stage.code}`}>
                <ShieldCheck size={14} /> Approve & lock
              </button>
              <button className="btn btn-danger" onClick={() => decide("reject")} disabled={busyApprove} data-testid={`stage-reject-${stage.code}`}>
                <Shield size={14} /> Send back
              </button>
            </div>
          </div>
        )}
        {stage.status === "approved" && (
          <div className="pf-card" style={{ padding: 12, marginTop: 18, borderColor: "var(--pf-accent)", display: "flex", alignItems: "center", gap: 10 }}>
            <CheckCircle2 size={16} color="var(--pf-accent)" />
            <div style={{ fontSize: 13, color: "var(--pf-text)" }}>Approved. This stage is locked from re-runs unless reopened.</div>
          </div>
        )}

        {/* Wave V.5 — auto-queued downstream stages surfaced on approval */}
        {approvedNext?.next?.length > 0 && (
          <div
            className="pf-card"
            style={{ padding: 14, marginTop: 14, borderColor: "var(--pf-spark)", background: "color-mix(in srgb, var(--pf-spark) 8%, transparent)" }}
            data-testid={`stage-next-unblocked-${stage.code}`}
          >
            <div className="pf-overline" style={{ color: "var(--pf-spark)", marginBottom: 6 }}>
              NEXT UNBLOCKED · auto-queued
            </div>
            <div style={{ fontSize: 13, color: "var(--pf-text)", marginBottom: 8 }}>
              {approvedNext.message}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {approvedNext.next.map((nxt) => (
                <button
                  key={nxt.code}
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setApprovedNext(null);
                    try { sessionStorage.setItem("rachana.stage.activate", nxt.code); } catch { /* ignored */ }
                    // Re-assign via onChange → parent reload → rail re-renders,
                    // but we want to switch actively. Rely on the sessionStorage
                    // signal that StageWorkspaceTab already picks up on mount.
                    window.dispatchEvent(new CustomEvent("rachana.stage.activate", { detail: { code: nxt.code } }));
                    onChange?.();
                  }}
                  data-testid={`stage-next-open-${nxt.code}`}
                  style={{ fontSize: 12, fontWeight: 600 }}
                >
                  Open {nxt.code} — {nxt.name}
                </button>
              ))}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setApprovedNext(null)}
                data-testid={`stage-next-dismiss-${stage.code}`}
                style={{ fontSize: 12 }}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* VALIDATION ISSUES */}
        {validationIssues.length > 0 && (
          <div className="pf-card" style={{ padding: 14, marginTop: 18, borderColor: "var(--pf-spark)" }} data-testid={`stage-validation-${stage.code}`}>
            <div className="pf-overline" style={{ marginBottom: 8 }}>VALIDATION · {validationIssues.length} ISSUE{validationIssues.length !== 1 ? "S" : ""}</div>
            <div style={{ display: "grid", gap: 6 }}>
              {validationIssues.map((iss, i) => (
                <div key={i} style={{ padding: 8, borderLeft: `3px solid ${iss.severity === "error" ? "var(--pf-err)" : "var(--pf-spark)"}`, background: "var(--pf-surface-2)", borderRadius: 2 }}>
                  <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: iss.severity === "error" ? "var(--pf-err)" : "var(--pf-spark)" }}>{iss.code} · {iss.severity?.toUpperCase()}</div>
                  <div style={{ fontSize: 12.5, color: "var(--pf-text)", marginTop: 2 }}>{iss.message}</div>
                  <div style={{ fontSize: 10, color: "var(--pf-text-muted)", marginTop: 2 }}>{iss.standard}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PER-STAGE ATTACHMENTS */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 22, marginBottom: 6 }}>
          <div className="pf-overline" style={{ margin: 0 }}>ATTACHMENTS · {attachments.length}</div>
          <button type="button" className="btn btn-ghost" onClick={() => fileInputRef.current?.click()} data-testid={`stage-attach-${stage.code}`}>
            <Paperclip size={13} /> Upload
          </button>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const fd = new FormData(); fd.append("file", f);
              try {
                await api.post(`/projects/${projectId}/stages/${stage.code}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                setPollTick((t) => t + 1);
              } catch (e2) { setErr(formatErr(e2.response?.data?.detail) || e2.message); }
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
            data-testid={`stage-attach-input-${stage.code}`}
          />
        </div>
        {attachments.length === 0 ? (
          <div style={{ color: "var(--pf-text-muted)", fontSize: 12, fontStyle: "italic" }}>No attachments yet — drop vendor cuts, redlines, or surveys here.</div>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {attachments.map((a) => (
              <div key={a.id} className="pf-card" style={{ padding: 10, display: "flex", alignItems: "center", gap: 10 }} data-testid={`stage-att-${a.id}`}>
                <FileText size={14} color="var(--pf-spark)" />
                <div style={{ flex: 1, fontSize: 12 }}>
                  <div style={{ color: "var(--pf-text)" }}>{a.filename}</div>
                  <div style={{ color: "var(--pf-text-muted)", fontSize: 10 }}>{Math.round(a.size_bytes / 1024)} KB · {a.uploaded_at?.slice(0, 16)}</div>
                </div>
                <a className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 8px" }} href={`${apiRoot}/projects/${projectId}/stages/${stage.code}/attachments/${a.id}/download`}>
                  <Download size={12} /> Download
                </a>
                <button type="button" className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 8px" }}
                        onClick={async () => { await api.delete(`/projects/${projectId}/stages/${stage.code}/attachments/${a.id}`); setPollTick((t) => t + 1); }}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* COMMENTS THREAD */}
        <div className="pf-overline" style={{ marginTop: 22, marginBottom: 6 }}>DISCUSSION · {comments.length} COMMENT{comments.length !== 1 ? "S" : ""}</div>
        <div style={{ display: "grid", gap: 6, marginBottom: 8 }}>
          {comments.map((c) => (
            <div key={c.id} className="pf-card" style={{ padding: 10 }} data-testid={`stage-comment-${c.id}`}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 12, color: "var(--pf-spark)", fontWeight: 700 }}>{c.author_name || "Unknown"}</div>
                <div style={{ fontSize: 10, color: "var(--pf-text-muted)" }}>{c.created_at?.slice(0, 16)}</div>
              </div>
              <div style={{ fontSize: 13, color: "var(--pf-text)", marginTop: 4, whiteSpace: "pre-wrap" }}>{c.body}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <textarea
            className="pf-input pf-textarea"
            rows={2}
            placeholder="Add a comment, ask a question, or log an approval note…"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            data-testid={`stage-comment-input-${stage.code}`}
            style={{ fontSize: 12, flex: 1 }}
          />
          <button type="button" className="btn btn-primary" disabled={!newComment.trim()}
                  onClick={async () => {
                    try {
                      await api.post(`/projects/${projectId}/stages/${stage.code}/comments`, { body: newComment });
                      setNewComment(""); setPollTick((t) => t + 1);
                    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
                  }}
                  data-testid={`stage-comment-submit-${stage.code}`}>
            <MessageSquare size={13} /> Post
          </button>
        </div>

        {/* Deliverables list */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 24, marginBottom: 8 }}>
          <div className="pf-overline" style={{ margin: 0 }}>DELIVERABLES ({deliverables.length})</div>
          {deliverables.length > 0 && (
            <div style={{ display: "flex", gap: 4 }}>
              <a className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 8px" }}
                 href={`${apiRoot}/projects/${projectId}/stages/${stage.code}/handover.zip`}
                 data-testid={`stage-handover-${stage.code}`}>
                <Download size={12} /> Handover ZIP
              </a>
              <button type="button" className="btn btn-primary" style={{ fontSize: 11, padding: "4px 8px" }}
                      onClick={async () => {
                        try {
                          const r = await api.post(`/projects/${projectId}/stages/${stage.code}/sign`);
                          const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url; a.download = `${stage.code}_signed_manifest.json`;
                          document.body.appendChild(a); a.click(); a.remove();
                          setTimeout(() => URL.revokeObjectURL(url), 0);
                        } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
                      }}
                      data-testid={`stage-sign-${stage.code}`}>
                <FileSignature size={12} /> Sign &amp; Download
              </button>
            </div>
          )}
        </div>
        {deliverables.length === 0 ? (
          <div style={{ color: "var(--pf-text-muted)", fontSize: 13, fontStyle: "italic" }}>No deliverables yet — run the stage above.</div>
        ) : (
          <DeliverablesPicker
            deliverables={deliverables}
            openDeliv={openDeliv}
            setOpenDeliv={setOpenDeliv}
            projectId={projectId}
            stageCode={stage.code}
          />
        )}
        </>
        ))}
      </div>
      )}
    </div>
  );
}

export function DeliverableViewer({ d, projectId }) {
  const apiRoot = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
  // Only render as inline SVG when the content_md ACTUALLY begins with
  // <svg> tag. Many deliverables (E.3.1 v11+, etc.) carry
  // ``format: "svg"`` metadata while content_md contains the engineering
  // narrative as Markdown — rendering that via dangerouslySetInnerHTML
  // produces an unreadable 14 KB block of text on a beige page.
  const cm = (d.content_md || "");
  const isSvg = /^\s*<svg[\s>]/i.test(cm);
  // Drawing deliverables that *should* be viewed in the APS Viewer
  // (their actual SVG lives in `drawing_sheets` or `electrical_drawings`).
  // E.3.1 = Single Line Diagram; E.3.2 = SLD overview; F.4.* = loop
  // diagrams — surface a one-click "Open in Autodesk Forge Viewer" CTA
  // alongside the markdown narrative.
  const isSldDeliverable = /^E\.3(\.|$)/i.test(d.code || "");
  const downloadSvg = () => {
    const blob = new Blob([d.content_md || ""], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${d.code.replace(/\W+/g, "_")}.svg`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  const btnStyle = {
    fontSize: 12, padding: "6px 10px", display: "inline-flex", alignItems: "center", gap: 5,
    border: "1px solid var(--pf-border)", borderRadius: 4, background: "var(--pf-surface)",
    color: "var(--pf-text)", textDecoration: "none", fontWeight: 500,
  };
  return (
    <div data-testid={`deliv-content-${d.code}`}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
        <div>
          <div className="pf-overline" style={{ marginBottom: 2 }}>{d.code} · v{d.version || 1}</div>
          <div className="pf-heading" style={{ fontSize: 16, margin: 0 }}>{d.name}</div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <span className="pf-overline" style={{ color: "var(--pf-text-muted)", fontSize: 10, marginRight: 4 }}>DOWNLOAD</span>
          <a className="btn btn-ghost" style={btnStyle}
             href={`${apiRoot}/projects/${projectId}/deliverables/${d.id}/export.pdf`}
             target="_blank" rel="noopener noreferrer"
             data-testid={`deliv-pdf-${d.code}`}>
            <FileText size={14} /> PDF
          </a>
          <AsyncDocxButton
            apiRoot={apiRoot}
            projectId={projectId}
            deliverableId={d.id}
            deliverableCode={d.code}
            version={d.version || 1}
            style={btnStyle}
            testid={`deliv-docx-${d.code}`}
          />
          <a className="btn btn-ghost" style={btnStyle}
             href={`${apiRoot}/projects/${projectId}/docs/calculation/${d.id}/stampable.pdf`}
             target="_blank" rel="noopener noreferrer"
             data-testid={`deliv-stampable-${d.code}`}
             title="Stampable PDF with designer / checker / approver signature block — ready for wet-ink PE stamp">
            <FileSignature size={14} /> Stampable
          </a>
          {(d.precomputed || d.content_table) && (
            <a className="btn btn-ghost" style={btnStyle}
               href={`${apiRoot}/projects/${projectId}/deliverables/${d.id}/export.xlsx`}
               data-testid={`deliv-xlsx-${d.code}`}>
              <FileSpreadsheet size={14} /> Excel
            </a>
          )}
          {isSvg && (
            <button type="button" onClick={downloadSvg} style={btnStyle}
                    data-testid={`deliv-download-${d.code}`}>
              <ImageIcon size={14} /> SVG
            </button>
          )}
          {isSvg && (
            <a style={btnStyle}
               href={`${apiRoot}/projects/${projectId}/deliverables/${d.id}/export.dxf`}
               data-testid={`deliv-dxf-${d.code}`}>
              <Download size={14} /> DXF
            </a>
          )}
          {/* Wave CB — Per-deliverable Claude Opus 4.6 generation.
              On click → background task starts → polling until done. */}
          <AiGenerateButton
            projectId={projectId}
            deliverableCode={d.code}
            stageCode={d.stage_code}
            initialAiEnriched={d.ai_enriched}
            initialAiAt={d.ai_generated_at}
            btnStyle={btnStyle}
          />
        </div>
      </div>
      {isSvg ? (
        <SvgInlinePanZoom svg={d.content_md}
                          testId={`deliv-svg-${d.code}`}/>
      ) : (
        <>
          {/* SLD deliverables (E.3.x): show the actual diagram via APS Viewer
              instead of leaving the user with only the narrative. */}
          {isSldDeliverable && (
            <SldDeliverableViewer projectId={projectId}
                                   deliverableCode={d.code}/>
          )}
          {/* Wave BG — B.1.4 AI Assessment gets a visual gauge + per-product
              bar cards rendered above the narrative markdown. */}
          {d.precomputed?.ai_assessment && (
            <AIAssessmentCard assessment={d.precomputed.ai_assessment} />
          )}
          <div className="pf-md" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{d.content_md || "(empty)"}</ReactMarkdown>
          </div>
        </>
      )}
      {(() => {
        // Wave Y — hide UI-routing hints (opens_tab) from the user-facing
        // "Pre-computed numerics" block. Only show if we have real engineering
        // numerics like flow rates, sizing, durations, etc.
        const pre = d.precomputed;
        if (!pre || typeof pre !== "object") return null;
        const meaningfulKeys = Object.keys(pre).filter((k) => k !== "opens_tab" && k !== "ai_assessment");
        if (meaningfulKeys.length === 0) return null;
        const cleaned = Object.fromEntries(meaningfulKeys.map((k) => [k, pre[k]]));
        return (
          <details style={{ marginTop: 16 }} data-testid={`deliv-precomputed-${d.code}`}>
            <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--pf-spark)", fontFamily: "'JetBrains Mono', monospace" }}>
              Pre-computed numerics (deterministic engineering calculations)
            </summary>
            <pre style={{ marginTop: 8, padding: 12, background: "var(--pf-surface-2)", borderRadius: 4, overflow: "auto", fontSize: 11, color: "var(--pf-text)" }}>
              {JSON.stringify(cleaned, null, 2)}
            </pre>
          </details>
        );
      })()}
      {/* Wave Y — Source-of-truth panel: what inputs / upstream deliverables
          were used to generate this document. Each deliverable now carries an
          optional `sources` array (e.g. ["A.1 DBD §3.2", "A.4 Site Data"]) plus
          `assumptions` (LLM-driven mappings) for full traceability. */}
      {(d.sources || d.assumptions) && (
        <details style={{ marginTop: 12 }} data-testid={`deliv-sources-${d.code}`}>
          <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--pf-accent)", fontFamily: "'JetBrains Mono', monospace" }}>
            🔗 Source / inputs used
          </summary>
          <div style={{ marginTop: 8, padding: 12, background: "var(--pf-bg)",
                              border: "1px solid var(--pf-border)", borderRadius: 4, fontSize: 11.5 }}>
            {d.sources && d.sources.length > 0 && (
              <>
                <div style={{ color: "var(--pf-text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Upstream deliverables consulted</div>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {d.sources.map((s, i) => (
                    <li key={i} style={{ marginBottom: 2 }}>
                      <code style={{ color: "var(--pf-spark)", fontSize: 11 }}>{typeof s === "string" ? s : (s.code || s.tag || JSON.stringify(s))}</code>
                      {typeof s !== "string" && s.note && <span style={{ marginLeft: 6, color: "var(--pf-text-muted)" }}>— {s.note}</span>}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {d.assumptions && d.assumptions.length > 0 && (
              <>
                <div style={{ color: "var(--pf-text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 8, marginBottom: 4 }}>Assumptions / mappings made by Capexara Process Industry AI</div>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {d.assumptions.map((a, i) => <li key={i} style={{ marginBottom: 2 }}>{a}</li>)}
                </ul>
              </>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

// ── Inline APS Viewer for SLD-class deliverables (E.3.x) ──

// ── Reusable inline SVG pan/zoom for deliverable previews ──
// Used by every SVG-format deliverable (2D plot plan, single line,
// loop drawings, structural plans, etc.) across every process-plant
// sub-industry and country profile. Adds Fit / +/- buttons, mouse
// wheel zoom toward cursor, click-drag pan.
/* eslint-disable */
function SvgInlinePanZoom({ svg, testId, height = 560 }) {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const fit = useCallback(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;
    const svgEl = inner.querySelector("svg");
    if (!svgEl) return;
    const vb = svgEl.viewBox?.baseVal;
    const sw = (vb && vb.width)  || svgEl.clientWidth  || 1;
    const sh = (vb && vb.height) || svgEl.clientHeight || 1;
    const k = Math.min(wrap.clientWidth / sw,
                       wrap.clientHeight / sh) * 0.96;
    setScale(k);
    setPan({ x: (wrap.clientWidth  - sw * k) / 2,
             y: (wrap.clientHeight - sh * k) / 2 });
  }, []);
  useEffect(() => {
    const t = setTimeout(fit, 60); return () => clearTimeout(t);
  }, [svg, fit]);
  const onWheel = (e) => {
    e.preventDefault();
    const r = wrapRef.current.getBoundingClientRect();
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    const delta = -e.deltaY * 0.0015;
    const ns = Math.max(0.05, Math.min(20, scale * (1 + delta)));
    setPan({ x: pan.x - (mx - pan.x) * (ns / scale - 1),
             y: pan.y - (my - pan.y) * (ns / scale - 1) });
    setScale(ns);
  };
  const onMouseDown = (e) => {
    if (e.button > 1) return;
    dragRef.current = { x: e.clientX, y: e.clientY,
                          px: pan.x, py: pan.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setPan({ x: dragRef.current.px + (e.clientX - dragRef.current.x),
             y: dragRef.current.py + (e.clientY - dragRef.current.y) });
  };
  const onMouseUp = () => { dragRef.current = null; };
  const pill = {
    background: "transparent", color: "#e2e8f0",
    border: "1px solid rgba(255,255,255,0.12)",
    padding: "5px 12px", borderRadius: 999, cursor: "pointer",
    fontSize: 13, lineHeight: 1, fontFamily: "monospace" };
  return (
    <div data-testid={testId}
         style={{ position: "relative", height,
                     background: "#FAF9F6",
                     borderRadius: 4, overflow: "hidden",
                     border: "1px solid var(--pf-border)" }}>
      <div ref={wrapRef}
           onWheel={onWheel}
           onMouseDown={onMouseDown}
           onMouseMove={onMouseMove}
           onMouseUp={onMouseUp}
           onMouseLeave={onMouseUp}
           style={{ position: "absolute", inset: 0,
                       overflow: "hidden",
                       cursor: dragRef.current ? "grabbing" : "grab",
                       touchAction: "none" }}>
        <div ref={innerRef}
             style={{ position: "absolute", top: 0, left: 0,
                         transform: `translate(${pan.x}px, ${pan.y}px) `
                                  + `scale(${scale})`,
                         transformOrigin: "0 0",
                         transition: dragRef.current ? "none"
                                                     : "transform 80ms" }}
             dangerouslySetInnerHTML={{ __html: svg }} />
      </div>
      <div data-testid={`${testId}-toolbar`}
           style={{ position: "absolute", bottom: 14, left: "50%",
                       transform: "translateX(-50%)", zIndex: 10,
                       display: "flex", gap: 4, padding: "4px 6px",
                       borderRadius: 999,
                       background: "rgba(15,23,41,0.85)",
                       backdropFilter: "blur(10px)",
                       border: "1px solid rgba(255,255,255,0.1)",
                       boxShadow: "0 6px 24px rgba(0,0,0,0.35)" }}
           onMouseDown={(e) => e.stopPropagation()}>
        <button data-testid={`${testId}-zoom-out`}
                onClick={() => setScale((s) => Math.max(0.05, s * 0.8))}
                style={pill}>−</button>
        <button data-testid={`${testId}-fit`} onClick={fit}
                title="Fit to page"
                style={{ ...pill, minWidth: 60, fontSize: 11 }}>
          Fit · {Math.round(scale * 100)}%
        </button>
        <button data-testid={`${testId}-zoom-in`}
                onClick={() => setScale((s) => Math.min(20, s * 1.25))}
                style={pill}>+</button>
      </div>
    </div>
  );
}
/* eslint-enable */


// The actual SLD lives in ``sld_layouts`` / ``electrical_drawings`` — not
// in the deliverable's content_md. Hitting ``/sld/sheets/.../render-svg``
// pushes the latest editor state into ``drawing_sheets``, after which
// the existing ApsDrawingViewer can translate it to SVF2 just like
// PFD / P&ID flows.
function SldDeliverableViewer({ projectId, deliverableCode }) {
  const [sheetId, setSheetId] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const reg = await api.get(`/projects/${projectId}/sld/register`);
        const sheets = reg.data?.sheets || [];
        const target = sheets[0]?.sheet_no || "SLD-Main-001";
        // Render the SVG once so drawing_sheets is populated for APS.
        await api.post(
          `/projects/${projectId}/sld/sheets/${encodeURIComponent(target)}/render-svg`);
        if (!cancelled) setSheetId(target);
      } catch (e) {
        if (!cancelled) setErr(formatErr(e?.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  return (
    <div data-testid={`sld-deliv-viewer-${deliverableCode}`}
         style={{ height: 540, display: "flex", flexDirection: "column",
                     marginBottom: 16, borderRadius: 6, overflow: "hidden",
                     border: "1px solid var(--pf-border)" }}>
      {busy && (
        <div style={{ flex: 1, display: "flex", alignItems: "center",
                          justifyContent: "center", color: "var(--pf-muted)",
                          gap: 8, fontSize: 12 }}>
          <Loader2 size={16} className="animate-spin"
              style={{ color: "var(--pf-spark)" }}/>
          Preparing Autodesk Forge view of the SLD…
        </div>
      )}
      {err && !busy && (
        <div style={{ padding: 12, color: "#fca5a5", fontSize: 12,
                          background: "rgba(127,29,29,0.18)" }}>
          {err}
        </div>
      )}
      {!busy && !err && sheetId && (
        <ApsDrawingViewer
            projectId={projectId} sheetId={sheetId}
            docKind="sld" title="Single Line Diagram"/>
      )}
    </div>
  );
}


function StageStandardsBar({ projectId, stageCode }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/projects/${projectId}/stages/${stageCode}/standards`).then((r) => {
      if (alive) setData(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, [projectId, stageCode]);
  if (!data) return null;
  const builtin = data.standards_pack || {};
  const codes = Object.keys(builtin);
  const customCount = (data.custom_standards || []).length;
  if (codes.length === 0 && customCount === 0) return null;
  return (
    <div style={{ marginTop: 8 }} data-testid={`stage-standards-${stageCode}`}>
      <button type="button"
              onClick={() => setOpen((v) => !v)}
              style={{
                background: "transparent", border: "none", cursor: "pointer", padding: 0,
                fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "var(--pf-spark)",
              }}
              data-testid={`stage-standards-toggle-${stageCode}`}>
        STANDARDS · {codes.length} built-in{customCount > 0 ? ` + ${customCount} custom` : ""} · {open ? "hide" : "show"}
      </button>
      {open && (
        <div className="pf-card" style={{ padding: 10, marginTop: 6, fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
          {codes.length > 0 && (
            <>
              <div className="pf-overline" style={{ marginBottom: 6 }}>BUILT-IN PACK</div>
              <div style={{ display: "grid", gap: 4 }}>
                {codes.map((k) => (
                  <div key={k} style={{ display: "flex", gap: 8 }}>
                    <span style={{ color: "var(--pf-spark)", minWidth: 110 }}>{k}</span>
                    <span style={{ color: "var(--pf-text-muted)" }}>{Array.isArray(builtin[k]) ? builtin[k].join(", ") : String(builtin[k])}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          {customCount > 0 && (
            <>
              <div className="pf-overline" style={{ margin: "10px 0 6px" }}>CUSTOM</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {data.custom_standards.map((c) => (
                  <li key={c.id || c.filename} style={{ color: "var(--pf-text)" }}>{c.filename}{c.discipline ? ` · ${c.discipline}` : ""}{c.description ? ` — ${c.description}` : ""}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}



/** Inline expandable explainer for a stage's hard + soft dependencies.
 *
 *   Renders a tight one-line summary by default ("REQUIRES B.4 · PREFERS C.3").
 *   Clicking the summary expands a per-dep detail block where each dependency
 *   is shown alongside *why* it is required and *how its output is used*.
 */
function DependencyExplainer({ stageCode, hardDeps, softDeps, depReasons }) {
  const [open, setOpen] = useState(false);
  const hasReasons = (hardDeps.some((d) => depReasons[d])
                  || softDeps.some((d) => depReasons[d]));

  return (
    <div style={{ marginTop: 10 }} data-testid={`stage-deps-explainer-${stageCode}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "transparent", border: "none", padding: 0,
          cursor: hasReasons ? "pointer" : "default",
          display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
          fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
        }}
        data-testid={`stage-deps-explainer-toggle-${stageCode}`}
        disabled={!hasReasons}
      >
        {hardDeps.length > 0 && (
          <span style={{ color: "var(--pf-err)" }}>REQUIRES: {hardDeps.join(", ")}</span>
        )}
        {softDeps.length > 0 && (
          <span style={{ color: "var(--pf-text-muted)" }}>PREFERS: {softDeps.join(", ")}</span>
        )}
        {hasReasons && (
          <span style={{ color: "var(--pf-spark)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            · {open ? "hide why" : "why?"}
          </span>
        )}
      </button>

      {open && hasReasons && (
        <div
          className="pf-card"
          style={{
            marginTop: 8, padding: "10px 12px", fontSize: 12.5,
            color: "var(--pf-text-muted)", lineHeight: 1.55,
          }}
          data-testid={`stage-deps-explainer-body-${stageCode}`}
        >
          {hardDeps.filter((d) => depReasons[d]).length > 0 && (
            <>
              <div className="pf-overline" style={{ color: "var(--pf-err)", marginBottom: 6 }}>
                HARD DEPENDENCIES — output of these stages is required
              </div>
              <ul style={{ margin: "0 0 8px", paddingLeft: 18 }}>
                {hardDeps.map((d) => depReasons[d] && (
                  <li key={d} style={{ marginBottom: 6 }}>
                    <strong style={{ color: "var(--pf-err)", fontFamily: "'JetBrains Mono', monospace", marginRight: 6 }}>{d}</strong>
                    {depReasons[d]}
                  </li>
                ))}
              </ul>
            </>
          )}
          {softDeps.filter((d) => depReasons[d]).length > 0 && (
            <>
              <div className="pf-overline" style={{ color: "var(--pf-spark)", marginBottom: 6 }}>
                SOFT DEPENDENCIES — output is preferred (placeholders accepted)
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {softDeps.map((d) => depReasons[d] && (
                  <li key={d} style={{ marginBottom: 6 }}>
                    <strong style={{ color: "var(--pf-spark)", fontFamily: "'JetBrains Mono', monospace", marginRight: 6 }}>{d}</strong>
                    {depReasons[d]}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}


/** Read-only banner shown on L1 (parent) stage panels.
 *
 *   L1 stages roll up their L2 children's status — they are not directly
 *   runnable. This component shows the rollup progress and explains the
 *   pattern so users go run individual sub-stages instead.
 */
function ParentStageRollup({ stage, projectId, onPick }) {
  const total = stage.children_count || 0;
  const done = stage.children_complete || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  // Wave CG.3 — instead of just showing the "go run a sub-stage" message,
  // fetch the rolled-up deliverables across ALL sub-stages so the user
  // actually SEES what has been produced. This addresses the recurring
  // "I clicked run and nothing happened" complaint (the deliverables
  // were produced, they were just hidden behind a sub-stage navigation).
  const [rollup, setRollup] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!projectId || !stage?.code) return undefined;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(
          `/projects/${projectId}/stages/${stage.code}/rollup`);
        if (alive) setRollup(data);
      } catch (_e) {
        if (alive) setRollup(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId, stage?.code]);

  return (
    <div
      className="pf-card"
      style={{
        padding: 18, marginTop: 4, borderColor: "var(--pf-spark)",
        background: "var(--pf-surface)",
      }}
      data-testid={`stage-parent-rollup-${stage.code}`}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Lock size={16} color="var(--pf-spark)" />
        <div className="pf-heading" style={{ fontSize: 14, margin: 0, color: "var(--pf-spark)" }}>
          Parent stage — rolls up automatically
        </div>
      </div>
      <div style={{ fontSize: 13, color: "var(--pf-text-muted)", marginTop: 8, lineHeight: 1.55 }}>
        Stage <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>{stage.code}</strong> is a parent stage. All work
        happens at the L2 sub-stage level — open one of the sub-stages on the
        left rail (e.g. <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>{stage.code}.1</strong>) and run it.
        This stage will be marked complete once every sub-stage is approved.
      </div>
      {total > 0 && (
        <div style={{ marginTop: 14 }} data-testid={`stage-parent-progress-${stage.code}`}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--pf-text-muted)", marginBottom: 4 }}>
            <span>Sub-stage progress</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{done} / {total}  ({pct}%)</span>
          </div>
          <div style={{
            height: 8, borderRadius: 4, background: "var(--pf-bg-elevated)",
            border: "1px solid var(--pf-border)", overflow: "hidden",
          }}>
            <div style={{
              width: `${pct}%`, height: "100%",
              background: pct === 100 ? "var(--pf-ok, #4ade80)" : "var(--pf-spark)",
              transition: "width 240ms ease-out",
            }} />
          </div>
        </div>
      )}
      {/* Sub-stage deliverables roll-up */}
      <div style={{ marginTop: 18 }} data-testid={`stage-parent-rollup-list-${stage.code}`}>
        <div className="pf-overline"
            style={{ margin: 0, marginBottom: 10, color: "var(--pf-text)",
                        fontSize: 11, letterSpacing: 0.6 }}>
          Sub-stage Deliverables ({rollup?.total_deliverables ?? "…"})
        </div>
        {loading && (
          <div style={{ color: "var(--pf-text-muted)", fontSize: 12 }}>
            Loading sub-stage outputs…
          </div>
        )}
        {!loading && rollup && (rollup.sub_stages || []).length === 0 && (
          <div style={{ color: "var(--pf-text-muted)", fontSize: 12,
                          fontStyle: "italic" }}>
            No sub-stages have been run yet. Click any sub-stage on the
            left rail and press Run.
          </div>
        )}
        {!loading && rollup && (rollup.sub_stages || []).map((ss) => (
          <div key={ss.code} data-testid={`rollup-sub-${ss.code}`}
              style={{ marginBottom: 12, padding: "10px 12px",
                          background: "var(--pf-surface-2, rgba(255,255,255,0.03))",
                          border: "1px solid var(--pf-border)", borderRadius: 4 }}>
            <button type="button"
                onClick={() => onPick?.(ss.code)}
                data-testid={`rollup-sub-${ss.code}-open`}
                style={{ background: "transparent", border: "none",
                              padding: 0, cursor: "pointer",
                              color: "var(--pf-spark)", fontWeight: 700,
                              fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
                              marginBottom: 6 }}>
              {ss.code} · {ss.name}  →
            </button>
            <div style={{ fontSize: 11, color: "var(--pf-text-muted)",
                              marginBottom: 6 }}>
              {ss.status === "complete" ? "✓ Complete" : ss.status || "Not started"}
              {ss.deliverables?.length > 0 && (
                <span style={{ marginLeft: 8 }}>
                  · {ss.deliverables.length} deliverable
                  {ss.deliverables.length > 1 ? "s" : ""}
                </span>
              )}
            </div>
            {(ss.deliverables || []).map((d) => (
              <div key={d.code || d.id}
                  data-testid={`rollup-deliv-${d.code || d.id}`}
                  style={{ fontSize: 11.5, padding: "2px 0",
                              color: "var(--pf-text)",
                              fontFamily: "'JetBrains Mono', monospace" }}>
                <span style={{ color: "var(--pf-spark)",
                                      marginRight: 8 }}>{d.code || "—"}</span>
                <span>{d.name}</span>
                {d.format && (
                  <span style={{ color: "var(--pf-text-muted)",
                                        fontSize: 10, marginLeft: 6 }}>
                    [{d.format}]
                  </span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
